import React from "react";
import { Link } from "react-router-dom";
import pluralize from "pluralize";

import { useRoutes } from "utils/routes";
import Loading from "ui/Loading";
import Panel from "ui/Panel";
import Grid from "ui/Grid";
import Badge from "ui/Badge";
import { TargetTag } from "features/Checks/AlertDestinationTag";

import { useAlertDestinationListQuery } from "./gql/Query.generated";

function AlertDestinationList({
  organizationSlug,
}: {
  organizationSlug: string;
}) {
  const { organizationAlertDestination, organizationAlertDestinationNew } =
    useRoutes();
  const { data, loading, error } = useAlertDestinationListQuery({
    variables: { organizationSlug },
  });
  if (loading || error || !data) {
    return <Loading error={!!error} />;
  }

  return (
    <Panel
      title="Alert Destinations"
      secondaryTitle={
        <Link to={organizationAlertDestinationNew(organizationSlug)}>
          Add destination
        </Link>
      }
    >
      <Grid
        data={data.getAlertDestinations}
        striped
        defaultSortBy="name"
        columns={[
          {
            field: "name",
            header: "Name",
            width: "minmax(200px,1fr)",
            renderer: function NameCell({ rowData, fieldData }) {
              return (
                <>
                  <Link
                    to={organizationAlertDestination(
                      organizationSlug,
                      rowData.id,
                    )}
                  >
                    {fieldData}
                  </Link>
                  {rowData.isDefault && <Badge>default</Badge>}
                </>
              );
            },
          },
          {
            field: "integrations",
            header: "Targets",
            width: "minmax(200px,2fr)",
            renderer: function TargetsCell({ rowData }) {
              if (
                rowData.integrations.length === 0 &&
                rowData.memberCount === 0
              ) {
                return <span className="text-[#606771]">—</span>;
              }
              return (
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  {rowData.integrations.map((i) => (
                    <TargetTag
                      key={i.id}
                      mechanism={i.kind}
                      label={i.displayName}
                    />
                  ))}
                  {rowData.memberCount > 0 && (
                    <TargetTag
                      mechanism="email"
                      label={pluralize("member", rowData.memberCount, true)}
                    />
                  )}
                </div>
              );
            },
          },
        ]}
      />
    </Panel>
  );
}

export default AlertDestinationList;
